import axios from "axios"
import Joi from "joi"

import { LINKS } from "@api/links.const"
import { handleError } from "@utils/error.middleware"

import { type APIError } from "@interfaces/APIError.interface"
import { type Event } from "@utils/interfaces/Event.interface"
import { useErrorStore } from "@stores/models/error.store"

export type APIConfirmResponse = { success: boolean } | Event

interface APITypeEvent {
  events: Event[]
}

const COMMUNICATION_INTEGRITY_ERROR = "Communication integrity error"

const joiService = Joi.object({
  name: Joi.string().required(),
  color: Joi.string().allow(null, ""),
  description: Joi.string().allow(null, ""),
  duration: Joi.number().allow(null),
  allDay: Joi.boolean(),
  durationDays: Joi.number().allow(null),
  price: Joi.alternatives().try(Joi.number(), Joi.string()),
  selectedOptions: Joi.array().items(Joi.string())
})

const joiCompany = Joi.object({
  name: Joi.string().required(),
  subdomain: Joi.string().allow(null).required()
})

const joiAgenda = Joi.object({
  id: Joi.number().required(),
  name: Joi.string().required(),
  image: Joi.string().allow(null)
})

const joiEvent = Joi.object({
  id: Joi.number().required(),
  name: Joi.alternatives().try(Joi.string(), Joi.allow(null)).required(),
  price: Joi.alternatives().try(Joi.number(), Joi.string()).required(),
  // backend might return ISO string or a Date-like value
  dateStart: Joi.alternatives().try(Joi.date().iso(), Joi.string()).required(),
  confirmation: Joi.string().required(),
  eventGroupId: Joi.number().allow(null),
  waitingListPosition: Joi.number().allow(null),
  waitingListTotal: Joi.number().allow(null),
  waitingListPreferredInterval: Joi.string().allow(null),
  services: Joi.array().items(joiService).allow(null),
  company: joiCompany.allow(null),
  agenda: joiAgenda.allow(null)
})

export const APIEvent = {
  async getAllEvents(): Promise<APITypeEvent | APIError> {
    try {
      const response = await axios.post<APITypeEvent>(LINKS.EVENT.GET_ALL())

      const responseSchema = Joi.alternatives().try(
        Joi.object({ events: Joi.array().items(joiEvent).required() }),
        Joi.object({ error: Joi.string().required() })
      )

      const { error, value } = responseSchema.validate(response.data)
      if (error) {
        const errorStore = useErrorStore()
        errorStore.showIntegrityError()
        return { error: COMMUNICATION_INTEGRITY_ERROR }
      }

      return value
    } catch (error) {
      return handleError(error)
    }
  },

  async confirmEvent(eventId: number): Promise<APIConfirmResponse | APIError> {
    try {
      const response = await axios.post(LINKS.EVENT.CONFIRM(), { eventId })

      const responseSchema = Joi.alternatives().try(
        joiEvent,
        Joi.object({ success: Joi.boolean().required() }),
        Joi.object({ error: Joi.string().required() })
      )

      const { error, value } = responseSchema.validate(response.data)
      if (error) {
        const errorStore = useErrorStore()
        errorStore.showIntegrityError()
        return { error: COMMUNICATION_INTEGRITY_ERROR }
      }

      return value
    } catch (error) {
      return handleError(error)
    }
  },

  async cancelEvent(eventId: number): Promise<APIConfirmResponse | APIError> {
    try {
      const response = await axios.post(LINKS.EVENT.CANCEL(), { eventId })

      const responseSchema = Joi.alternatives().try(
        joiEvent,
        Joi.object({ success: Joi.boolean().required() }),
        Joi.object({ error: Joi.string().required() })
      )

      const { error, value } = responseSchema.validate(response.data)
      if (error) {
        const errorStore = useErrorStore()
        errorStore.showIntegrityError()
        return { error: COMMUNICATION_INTEGRITY_ERROR }
      }

      return value
    } catch (error) {
      return handleError(error)
    }
  }
}
